import { useEffect, useState, type ReactNode } from 'react';
import {
  SYNC_WINDOW_DAYS,
  parseSenderKey,
  senderKey,
  type CalendarSyncSettingsView,
  type MailSettingsView,
  type SyncSettings as SyncSettingsData,
} from '@ehr/shared';
import { api, ApiError } from '../lib/api.js';
import { Dd } from './Dropdown.js';
import { SaveButton, runSave, type SaveNotice } from './SaveToast.js';
import { syncErrText, syncSavedText } from '../lib/saveCards.js';

/**
 * ehr#83 - Data & settings > Email & calendar: the practice's switches for the two features built
 * on staff's connected Google / Microsoft accounts. Appointments on each clinician's own calendar
 * (calendar sync), and the practice's email sent from one connected mailbox. Admin / manager
 * only. Each section saves on its own (outside the modal's Save). Anything that puts client
 * details on a calendar or in an email needs the BAA acknowledgment first; the server's gates
 * enforce it on every send.
 *
 * ehr#681 follow-up: each section's Save reads "Saving…" for at least 2 s, then the drawer's save
 * card ("Calendar settings saved" / "Email settings saved", or "Not saved" with the reason) sits
 * above it, in place of the old inline "Saved." text.
 */

const msgOf = (e: unknown) => (e instanceof ApiError ? e.message : 'Could not save. Try again.');
const PROVIDER_LABEL: Record<string, string> = { google: 'Google', microsoft: 'Microsoft' };

function Toggle({
  title,
  desc,
  checked,
  disabled,
  onChange,
  id,
}: {
  title: string;
  desc: ReactNode;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
  id: string;
}) {
  return (
    <div className={disabled ? 'tog sync-tog-off' : 'tog'}>
      <div className="tog-txt">
        <label className="t" htmlFor={id}>
          {title}
        </label>
        <div className="d">{desc}</div>
      </div>
      <label className="sw">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span />
      </label>
    </div>
  );
}

function ServerNote({ live, what }: { live: boolean; what: string }) {
  if (live) return null;
  return (
    <p className="note warn sync-note">
      {what} is still switched off on the server while the Google and Microsoft account setup is
      finished. Your choices here are saved and apply as soon as it&apos;s on.
    </p>
  );
}

export function SyncSettings() {
  const [data, setData] = useState<SyncSettingsData | null>(null);
  const [loadErr, setLoadErr] = useState('');
  const [cal, setCal] = useState<CalendarSyncSettingsView | null>(null);
  const [mail, setMail] = useState<MailSettingsView | null>(null);
  const [busy, setBusy] = useState<'' | 'calendar' | 'mail'>('');
  const [note, setNote] = useState<{ calendar: SaveNotice | null; mail: SaveNotice | null }>({
    calendar: null,
    mail: null,
  });

  const adopt = (d: SyncSettingsData) => {
    setData(d);
    const c = d.calendar;
    setCal({
      enabled: c.enabled,
      includeClientDetail: c.includeClientDetail,
      inviteClient: c.inviteClient,
      baaAcknowledged: c.baaAcknowledged,
      windowDays: c.windowDays,
      practiceUserId: c.practiceUserId,
      practiceProvider: c.practiceProvider,
    });
    const m = d.mail;
    setMail({
      enabled: m.enabled,
      senderUserId: m.senderUserId,
      senderProvider: m.senderProvider,
      baaAcknowledged: m.baaAcknowledged,
    });
  };
  useEffect(() => {
    api
      .get<SyncSettingsData>('/sync-settings')
      .then(adopt)
      .catch((e: unknown) => setLoadErr(msgOf(e)));
  }, []);

  if (loadErr) return <p className="note warn">Could not load these settings: {loadErr}</p>;
  if (!data || !cal || !mail) return <div className="empty">Loading…</div>;

  const save = (which: 'calendar' | 'mail') => {
    if (busy) return;
    setBusy(which);
    setNote((n) => ({ ...n, [which]: null }));
    const req =
      which === 'calendar'
        ? api.put<SyncSettingsData>('/sync-settings/calendar', cal)
        : api.put<SyncSettingsData>('/sync-settings/mail', mail);
    // the settings show what the server kept as soon as it answers; the button and card wait 2 s
    void runSave(
      req.then((d) => {
        adopt(d);
        return d;
      }),
      { ok: syncSavedText(which), err: syncErrText },
    ).then((r) => {
      setNote((n) => ({ ...n, [which]: r.notice }));
      setBusy('');
    });
  };
  const closeNote = (which: 'calendar' | 'mail') => setNote((n) => ({ ...n, [which]: null }));

  // the BAA box clears the PHI options when it's unticked, so a save never carries one the gate ignores
  const setCalBaa = (v: boolean) =>
    setCal((c) =>
      c
        ? {
            ...c,
            baaAcknowledged: v,
            ...(v ? {} : { includeClientDetail: false, inviteClient: false }),
          }
        : c,
    );
  const setMailBaa = (v: boolean) =>
    setMail((m) => (m ? { ...m, baaAcknowledged: v, ...(v ? {} : { enabled: false }) } : m));

  // ehr#436: the practice calendar, one of the admins' connected calendars
  const pracCals = data.calendar.practiceCalendars ?? [];
  const pracKey = cal.practiceUserId ? `${cal.practiceProvider}:${cal.practiceUserId}` : '';
  const pracGone = !!pracKey && !pracCals.some((c) => senderKey(c) === pracKey);
  const senders = data.mail.senders;
  const currentKey = mail.senderUserId ? `${mail.senderProvider}:${mail.senderUserId}` : '';
  const senderGone = !!currentKey && !senders.some((s) => senderKey(s) === currentKey);
  const canSend = mail.baaAcknowledged && !!mail.senderUserId && !senderGone;

  return (
    <>
      <div className="sec sync-sec" id="sync-calendar">
        <div className="sec-head">
          <h4>Appointments on staff calendars</h4>
          <span>{data.calendar.connectedCalendars} staff with a connected calendar</span>
        </div>
        <ServerNote live={data.calendar.live} what="Calendar sync" />
        <Toggle
          id="syncCalOn"
          title="Put appointments on each person's own calendar"
          desc="Everyone who connected a calendar above gets their own upcoming appointments, kept up to date when they move or are cancelled."
          checked={cal.enabled}
          onChange={(v) => setCal({ ...cal, enabled: v })}
        />
        <div className="tog">
          <div className="tog-txt">
            <div className="t">Practice calendar</div>
            <div className="d">
              {pracCals.length
                ? 'Every appointment in the practice also goes on this admin’s calendar (a front-desk view), even when the switch above is off.'
                : 'No admin has connected a calendar yet. An admin connects one under Calendars above.'}
              {pracGone && (
                <span className="sync-gone">
                  {' '}
                  The calendar picked before is no longer an admin&apos;s connected calendar.
                </span>
              )}
            </div>
          </div>
          <div className="sync-dd sync-dd-wide">
            <Dd
              value={pracGone ? '' : pracKey}
              onChange={(v) => {
                const k = parseSenderKey(v);
                setCal({
                  ...cal,
                  practiceUserId: k.senderUserId,
                  practiceProvider: k.senderProvider,
                });
              }}
              searchable={false}
              ariaLabel="Practice calendar"
              placeholder="Pick a calendar"
              disabled={!pracCals.length}
              options={[
                { value: '', label: 'No practice calendar' },
                ...pracCals.map((c) => ({
                  value: senderKey(c),
                  label: `${c.name} (${PROVIDER_LABEL[c.provider]})${c.accountEmail ? ` · ${c.accountEmail}` : ''}`,
                })),
              ]}
            />
          </div>
        </div>
        <div className="tog">
          <div className="tog-txt">
            <div className="t">How far ahead</div>
            <div className="d">Appointments in this window are kept on the calendars.</div>
          </div>
          <div className="sync-dd">
            <Dd
              value={String(cal.windowDays)}
              onChange={(v) => setCal({ ...cal, windowDays: Number(v) })}
              searchable={false}
              ariaLabel="How far ahead"
              options={SYNC_WINDOW_DAYS.map((d) => ({ value: String(d), label: `${d} days` }))}
            />
          </div>
        </div>
        <label className="chk sync-baa">
          <input
            type="checkbox"
            checked={cal.baaAcknowledged}
            onChange={(e) => setCalBaa(e.target.checked)}
          />
          <span>
            The connected calendars are Google Workspace or Microsoft 365 accounts covered by our
            BAA. <em>A personal Gmail or Outlook.com account can&apos;t be.</em>
          </span>
        </label>
        <Toggle
          id="syncCalDetail"
          title="Show the client's name and service on the event"
          desc={
            cal.baaAcknowledged
              ? 'Otherwise the event just says "Session".'
              : 'Needs the BAA box above. Until then the event just says "Session".'
          }
          checked={cal.includeClientDetail}
          disabled={!cal.baaAcknowledged}
          onChange={(v) => setCal({ ...cal, includeClientDetail: v })}
        />
        <Toggle
          id="syncCalInvite"
          title="Invite the client to the event"
          desc={
            cal.baaAcknowledged
              ? 'Only clients who agreed to email and have an address get the invite.'
              : 'Needs the BAA box above.'
          }
          checked={cal.inviteClient}
          disabled={!cal.baaAcknowledged}
          onChange={(v) => setCal({ ...cal, inviteClient: v })}
        />
        <div className="btn-row sync-foot save-host">
          <SaveButton
            label="Save calendar settings"
            busy={busy === 'calendar'}
            disabled={!!busy}
            notice={note.calendar}
            onCloseNotice={() => closeNote('calendar')}
            onClick={() => save('calendar')}
          />
        </div>
      </div>

      <div className="sec sync-sec" id="sync-mail">
        <div className="sec-head">
          <h4>Practice email from a connected mailbox</h4>
          <span>
            {senders.length} mailbox{senders.length === 1 ? '' : 'es'} can send
          </span>
        </div>
        <ServerNote live={data.mail.live} what="Sending from a mailbox" />
        <div className="tog">
          <div className="tog-txt">
            <div className="t">Send from</div>
            <div className="d">
              {senders.length
                ? 'The practice’s email (reminders and the like) goes out from this admin’s connected mailbox.'
                : 'No admin has connected a mailbox for sending yet. An admin connects one under Mailboxes above.'}
              {senderGone && (
                <span className="sync-gone">
                  {' '}
                  The mailbox picked before is no longer an admin&apos;s connected mailbox.
                </span>
              )}
            </div>
          </div>
          <div className="sync-dd sync-dd-wide">
            <Dd
              value={senderGone ? '' : currentKey}
              onChange={(v) => setMail({ ...mail, ...parseSenderKey(v) })}
              searchable={false}
              ariaLabel="Send from"
              placeholder="Pick a mailbox"
              disabled={!senders.length}
              options={[
                { value: '', label: 'No mailbox' },
                ...senders.map((s) => ({
                  value: senderKey(s),
                  label: `${s.name} (${PROVIDER_LABEL[s.provider]})${s.accountEmail ? ` · ${s.accountEmail}` : ''}`,
                })),
              ]}
            />
          </div>
        </div>
        <label className="chk sync-baa">
          <input
            type="checkbox"
            checked={mail.baaAcknowledged}
            onChange={(e) => setMailBaa(e.target.checked)}
          />
          <span>
            That mailbox is a Google Workspace or Microsoft 365 account covered by our BAA.{' '}
            <em>A personal Gmail or Outlook.com account can&apos;t be.</em>
          </span>
        </label>
        <Toggle
          id="syncMailOn"
          title="Send the practice's email from this mailbox"
          desc={
            canSend
              ? 'Replies come back to that mailbox.'
              : 'Needs a mailbox and the BAA box above.'
          }
          checked={mail.enabled}
          disabled={!canSend}
          onChange={(v) => setMail({ ...mail, enabled: v })}
        />
        <div className="btn-row sync-foot save-host">
          <SaveButton
            label="Save email settings"
            busy={busy === 'mail'}
            disabled={!!busy}
            notice={note.mail}
            onCloseNotice={() => closeNote('mail')}
            onClick={() => save('mail')}
          />
        </div>
      </div>
    </>
  );
}
